import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from 'sonner';

// --- IMPORTAÇÕES DAS PÁGINAS ---
import Landing from './pages/Landing'; 
import Login from './pages/Login';
import Register from './pages/Register';
import ClientDashboard from './pages/ClientDashboard';
import Profile from './pages/Profile';
import ProfileTech from './pages/ProfileTech'; 
import History from './pages/History';
import FindTechnician from './pages/FindTechnician';
import BecomeTechnician from './pages/BecomeTechnician';
import TechnicianDashboard from './pages/TechnicianDashboard';
import AdminDashboard from './pages/AdminDashboard';
import SeedUsers from './pages/SeedUsers';
import ProfileView from './pages/ProfileView';
import DirectOrder from './pages/DirectOrder';
import Notification from './pages/Notification';
import Support from './pages/Support'; 
import Teste from './pages/Teste';

// --- IMPORTAÇÃO DA NOVA PÁGINA DE PAGAMENTO ---
import CheckoutPremium from './pages/CheckoutPremium'; 

// --- IMPORTAÇÕES DAS PÁGINAS DO ADMIN ---
import AdminClients from './admin/AdminClients';
import AdminTecs from './admin/AdminTecs';
import AdminActTecs from './admin/AdminActTecs';
import AdminOrders from './admin/AdminOrders';
import AdminFunctions from './admin/AdminFunctions';
import AdminLogs from './admin/AdminLogs';
import AdminSupport from './admin/AdminSupport';
import AdminSuspects from './admin/AdminSuspects';

// --- IMPORT DA IMAGEM DE FUNDO ---
import planoFundo from './assets/planodefundo.png';

import './App.css';

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, userProfile, loading } = useAuth();

  // 1. Enquanto estiver carregando os dados do Firebase, exibe o loading na tela
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // 2. Se o usuário não estiver logado, manda direto para a tela de login
  if (!user) return <Navigate to="/login" replace />;
  
  const userRole = userProfile?.userType?.toLowerCase();
  
  // Administradores possuem passe livre irrestrito por todas as telas do sistema
  if (userRole === 'admin') return children;
  
  // 3. SE FOR TÉCNICO: Aplicamos a verificação cadastral e de tempo de assinatura
  if (userRole === 'technician') {
    
    // Se ele tentar acessar uma rota de técnico mas NÃO foi aprovado pelo Admin geral:
    if (allowedRoles && allowedRoles.includes('technician') && userProfile?.technicianStatus !== 'approved') {
      return <Navigate to="/dashboard" replace />;
    }

    // Se o cadastro está aprovado, mas a assinatura NÃO está ativa (ou expirou pelo tempo):
    if (userProfile?.paymentStatus !== 'active') {
      // Se ele já estiver na página de pagamento, deixa ele ficar lá! (Isso mata o looping)
      if (window.location.pathname === '/checkout-premium') {
        return children;
      }
      return <Navigate to="/checkout-premium" replace />;
    }

    // Se a assinatura dele ESTÁ ativa, mas ele tentar entrar por engano na tela de pagamento:
    if (window.location.pathname === '/checkout-premium') {
      return <Navigate to="/technician-dashboard" replace />;
    }
  }
  
  // Verificação padrão de papéis para as outras rotas normais do sistema
  if (allowedRoles && !allowedRoles.includes(userRole)) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="min-h-screen relative overflow-x-hidden">
          {/* CAMADA DO FUNDO GLOBAL PRESERVADA */}
          <div 
            className="fixed inset-0 pointer-events-none z-0 opacity-[0.06]" 
            style={{ 
              backgroundImage: `url(${planoFundo})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundColor: '#f8fafc'
            }}
          />

          <div className="relative z-10">
            <Toaster position="top-right" richColors closeButton />

            <Routes>
              {/* --- ROTAS PÚBLICAS --- */}
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<Login />} />
             <Route path="/register" element={<Register />} />
              <Route path="/seed-users" element={<SeedUsers />} />
              <Route path="/teste-financeiro" element={<Teste />} />

              {/* --- ÁREA COMPARTILHADA --- */}
              <Route path="/dashboard" element={<ProtectedRoute><ClientDashboard /></ProtectedRoute>} />
              <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
              <Route path="/history" element={<ProtectedRoute><History /></ProtectedRoute>} />
              <Route path="/notifications" element={<ProtectedRoute><Notification /></ProtectedRoute>} />
              <Route path="/support" element={<ProtectedRoute><Support /></ProtectedRoute>} />
              
              {/* Perfil e Pedidos */}
              <Route path="/profile/:techId" element={<ProtectedRoute><ProfileView /></ProtectedRoute>} />
              <Route path="/direct-order/:techId" element={<ProtectedRoute><DirectOrder /></ProtectedRoute>} />

              {/* ROTA DA TELA DE BLOQUEIO / PAGAMENTO */}
              <Route path="/checkout-premium" element={<ProtectedRoute><CheckoutPremium /></ProtectedRoute>} />

              <Route path="/profile-tech" element = {
                <ProtectedRoute allowedRoles={['technician']}>
                  <ProfileTech />
                </ProtectedRoute>
              } />

              <Route path="/find-technician" element={<ProtectedRoute><FindTechnician /></ProtectedRoute>} />
              <Route path="/become-technician" element={<ProtectedRoute><BecomeTechnician /></ProtectedRoute>} />

              {/* --- ÁREA DO TÉCNICO --- */}
              <Route path="/technician-dashboard" element = {
                <ProtectedRoute allowedRoles={['technician']}>
                  <TechnicianDashboard />
                </ProtectedRoute>
              } />

              {/* --- ÁREAS DO ADMIN --- */}
              <Route path="/admin-dashboard" element={<ProtectedRoute allowedRoles={['admin']}><AdminDashboard /></ProtectedRoute>} />
              <Route path="/admin-clients" element={<ProtectedRoute allowedRoles={['admin']}><AdminClients /></ProtectedRoute>} />
              <Route path="/admin-tecs" element={<ProtectedRoute allowedRoles={['admin']}><AdminTecs /></ProtectedRoute>} />
              <Route path="/admin-act-tecs" element={<ProtectedRoute allowedRoles={['admin']}><AdminActTecs /></ProtectedRoute>} />
              <Route path="/admin-orders" element={<ProtectedRoute allowedRoles={['admin']}><AdminOrders /></ProtectedRoute>} />
              <Route path="/admin-functions" element={<ProtectedRoute allowedRoles={['admin']}><AdminFunctions /></ProtectedRoute>} />
              <Route path="/admin-logs" element={<ProtectedRoute allowedRoles={['admin']}><AdminLogs /></ProtectedRoute>} />
              <Route path="/admin-support" element={<ProtectedRoute allowedRoles={['admin']}><AdminSupport /></ProtectedRoute>} />
              <Route path="/admin-suspects" element={<ProtectedRoute allowedRoles={['admin']}><AdminSuspects /></ProtectedRoute>} />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;